/**
 * NotificationsPage - Admin management for digest emails and push broadcasts
 * @atomic @idempotent
 */
import { memo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { BellIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import AdminHeader from '../../components/admin/AdminHeader'

const logger = createLogger('NotificationsPage')

const NotificationsPage = memo(function NotificationsPage() {
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()

  const [digestStats, setDigestStats] = useState({ optedIn: 0, total: 0 })
  const [pushStats, setPushStats] = useState({ activeSubscriptions: 0, uniqueUsers: 0 })
  const [statsLoading, setStatsLoading] = useState(true)

  const [pushForm, setPushForm] = useState({ title: '', body: '', url: '' })
  const [digestSending, setDigestSending] = useState(false)
  const [digestBulking, setDigestBulking] = useState(false)
  const [pushSending, setPushSending] = useState(false)

  const loadStats = useCallback(async () => {
    setStatsLoading(true)
    await execute(
      async () => {
        const [digestRes, pushRes] = await Promise.all([
          adminAPI.getDigestStats(),
          adminAPI.getPushStats()
        ])
        setDigestStats(digestRes.data)
        setPushStats(pushRes.data)
      },
      {
        onError: (err) => logger.error('Failed to load notification stats', err),
        silent: true
      }
    )
    setStatsLoading(false)
  }, [execute])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  const handleSendDigest = useCallback(async () => {
    if (!window.confirm(t('admin.notifications.digest.confirmSend'))) return
    setDigestSending(true)
    await execute(
      () => adminAPI.sendDigest(),
      {
        onSuccess: (res) => {
          const { sent, total } = res.data
          toast.success(t('admin.notifications.digest.sendSuccess', { sent, total }))
          logger.info('Digest sent', { sent, total })
        },
        onError: (err) => {
          toast.error(t('admin.notifications.digest.sendError'))
          logger.error('Digest send failed', err)
        }
      }
    )
    setDigestSending(false)
  }, [execute, t])

  const handleBulkDigest = useCallback(async (enabled) => {
    const confirmKey = enabled
      ? t('admin.notifications.digest.confirmOptAllIn')
      : t('admin.notifications.digest.confirmOptAllOut')
    if (!window.confirm(confirmKey)) return
    setDigestBulking(true)
    await execute(
      () => adminAPI.bulkUpdateDigest(enabled),
      {
        onSuccess: (res) => {
          const { updated } = res.data
          toast.success(t(
            enabled
              ? 'admin.notifications.digest.bulkOptInSuccess'
              : 'admin.notifications.digest.bulkOptOutSuccess',
            { count: updated }
          ))
          logger.info('Bulk digest update', { enabled, updated })
          loadStats()
        },
        onError: (err) => {
          toast.error(t('admin.notifications.digest.bulkError'))
          logger.error('Bulk digest update failed', err)
        }
      }
    )
    setDigestBulking(false)
  }, [execute, t, loadStats])

  const handleBroadcastPush = useCallback(async (e) => {
    e.preventDefault()
    if (!pushForm.title.trim() || !pushForm.body.trim()) return
    if (!window.confirm(t('admin.notifications.push.confirmBroadcast', { count: pushStats.activeSubscriptions }))) return
    setPushSending(true)
    await execute(
      () => adminAPI.broadcastPush(pushForm),
      {
        onSuccess: (res) => {
          const { sent } = res.data
          toast.success(t('admin.notifications.push.broadcastSuccess', { sent }))
          logger.info('Push broadcast sent', { sent })
          setPushForm({ title: '', body: '', url: '' })
        },
        onError: (err) => {
          toast.error(t('admin.notifications.push.broadcastError'))
          logger.error('Push broadcast failed', err)
        }
      }
    )
    setPushSending(false)
  }, [execute, pushForm, pushStats.activeSubscriptions, t])

  const handlePushFormChange = useCallback((e) => {
    const { name, value } = e.target
    setPushForm((prev) => ({ ...prev, [name]: value }))
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.notifications.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('admin.notifications.description')}</p>
        </div>

        {/* Digest Email Card */}
        <div className="bg-white rounded-lg shadow-sm border border-indigo-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <EnvelopeIcon className="h-6 w-6 text-indigo-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.notifications.digest.title')}</h2>
          </div>

          {statsLoading ? (
            <div className="h-8 bg-gray-100 animate-pulse rounded w-48" />
          ) : (
            <p className="text-sm text-gray-600 mb-4">
              {t('admin.notifications.digest.optInStat', { optedIn: digestStats.optedIn, total: digestStats.total })}
            </p>
          )}

          <p className="text-sm text-gray-500 mb-6">{t('admin.notifications.digest.description')}</p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSendDigest}
              onTouchEnd={handleSendDigest}
              disabled={digestSending || digestBulking || statsLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {digestSending ? t('admin.notifications.digest.sending') : t('admin.notifications.digest.sendButton')}
            </button>
            <button
              onClick={() => handleBulkDigest(true)}
              onTouchEnd={() => handleBulkDigest(true)}
              disabled={digestBulking || digestSending || statsLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('admin.notifications.digest.optAllInButton')}
            </button>
            <button
              onClick={() => handleBulkDigest(false)}
              onTouchEnd={() => handleBulkDigest(false)}
              disabled={digestBulking || digestSending || statsLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('admin.notifications.digest.optAllOutButton')}
            </button>
          </div>
        </div>

        {/* Push Notifications Card */}
        <div className="bg-white rounded-lg shadow-sm border border-orange-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <BellIcon className="h-6 w-6 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.notifications.push.title')}</h2>
          </div>

          {statsLoading ? (
            <div className="h-8 bg-gray-100 animate-pulse rounded w-64" />
          ) : (
            <p className="text-sm text-gray-600 mb-4">
              {t('admin.notifications.push.statSummary', {
                subscriptions: pushStats.activeSubscriptions,
                users: pushStats.uniqueUsers
              })}
            </p>
          )}

          <form onSubmit={handleBroadcastPush} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.notifications.push.titleLabel')} *
              </label>
              <input
                type="text"
                name="title"
                value={pushForm.title}
                onChange={handlePushFormChange}
                required
                maxLength={80}
                placeholder={t('admin.notifications.push.titlePlaceholder')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.notifications.push.bodyLabel')} *
              </label>
              <textarea
                name="body"
                value={pushForm.body}
                onChange={handlePushFormChange}
                required
                maxLength={200}
                rows={3}
                placeholder={t('admin.notifications.push.bodyPlaceholder')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.notifications.push.urlLabel')}
              </label>
              <input
                type="url"
                name="url"
                value={pushForm.url}
                onChange={handlePushFormChange}
                placeholder={t('admin.notifications.push.urlPlaceholder')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <button
              type="submit"
              disabled={pushSending || statsLoading || pushStats.activeSubscriptions === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {pushSending ? t('admin.notifications.push.sending') : t('admin.notifications.push.broadcastButton')}
            </button>
            {pushStats.activeSubscriptions === 0 && !statsLoading && (
              <p className="text-xs text-gray-400">{t('admin.notifications.push.noSubscribers')}</p>
            )}
          </form>
        </div>

      </div>
    </div>
  )
})

export default NotificationsPage
